import React, { useMemo, useState, useRef, useEffect } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import moment from 'moment'
import gql from 'graphql-tag'
import { useTranslation } from 'react-i18next'
import ContentBlock, { ExpandableBlocks } from 'components/ContentBlock'
import { Form, Select, DateTimePicker, Checkbox, Text } from 'components/forms'
import Button from 'components/Button'
import Divider from 'components/Divider'
import { useMutation, useLazyQuery, useQuery } from 'components/graphql'
import mutation from '../../gql/exportBills'
import { exportDSFin as exportDSFinMutation, getDSFinExport } from '../../gql/exportDSFin'
import exportDatevQuery from '../../gql/exportDatev'
import exportAbacusQuery from '../../gql/exportAbacus'
import downloadDatevQuery from '../../gql/downloadDatev'
import downloadAbacusQuery from '../../gql/downloadAbacus'
import getNameOfGqlQuery from 'helpers/getNameOfGqlQuery'
import { StatusIndicator } from 'components/graphql'
import { AlertModal, WarningModal, ServiceModal } from 'components/Modal'
import { listShops } from 'gql/query'
import getAppConfig from '../../gql/getAppConfig'
import Bexio from './Bexio'
import Apaleo from './Apaleo'
import Frutico from './Frutico'
import { downloadFromUrl, downloadFromUrlPromise } from 'helpers/downloadFromUrl'

const queryCrs = gql`
	query listCashRegisters {
		cashAssist {
			listCashRegisters(activeOnly: true) {
				id
				name
			}
		}
	}
`

const Export = props => {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isDSFSubmitting, setIsDSFSubmitting] = useState(false)
	const [isDSFError, setIsDSFError] = useState(false)
	const [showNoDataModal, setShowNoDataModal] = useState(false)
	const [saveTabUrl, setSaveTabUrl] = useState(false)
	const [saveFile, setSaveFile] = useState(false)

	const [isDatevLoading, setIsDatevLoading] = useState(false)
	const [isDatevDownloadLoading, setIsDatevDownloadLoading] = useState(false)

	const [isAbacusLoading, setIsAbacusLoading] = useState(false)
	const [isAbacusDownloadLoading, setIsAbacusDownloadLoading] = useState(false)

	const [shopsData, setShopsData] = useState([])

	useQuery(listShops, {
		onCompleted: res => {
			const defaultValue = {
				id: JSON.stringify(res?.cashAssist?.listShops?.items.map(o => o.id)),
				name: t('statistic_section.strings.all_shops')
			}
			const e = res.cashAssist?.listShops?.items || []
			setExportData(prevData => ({ ...prevData, shop: defaultValue }))
			setShopsData([ defaultValue, ...e ])
		}
	})
	useQuery(getAppConfig, {
		fetchPolicy: 'cache-first',
		onCompleted: res => {
			setExportData(prevData => ({ ...prevData, email: res.cashAssist?.getAppConfig?.cashRegisterGlobal?.savedEmail }))
		}
	})
	const [hasError, setHasError] = useState(false)
	const [warningMessages, setWarningMessages] = useState([])

	const formApi = useRef()
	const [t] = useTranslation()
	const saveTabRef = useRef()
	const startDay = useMemo(() => moment().utc().startOf('day').toISOString(), [])
	const endDay = useMemo(() => moment().utc().endOf('day').toISOString(), [])
	const initialValues = {
		date: moment().utc().startOf('day').toISOString()
	}

	const [exportData, setExportData] = useState({
		email: '',
		shop: {
			id: null,
			name: ''
		},
		datePeriod: {
			from: moment().subtract(1, 'months').utc().startOf('month').toISOString(),
			to: moment().subtract(1, 'months').utc().endOf('month').toISOString()
		}
	})

	const [mutate] = useMutation(mutation, {
		onCompleted: data => {
			setIsSubmitting(false)
			formApi.current.reset()
			if(saveFile) {
				const mutationName = getNameOfGqlQuery(mutation)
				const s3Key = data?.[mutationName]?.key
				if(s3Key) {
					const bucket = process.env.REACT_APP_STAGE === 'prod' ? 'hscustomer.s3-eu-west-1.amazonaws.com' : 'cashassist-api-dev-uploads.s3-eu-west-1.amazonaws.com'
					const link = `https://${bucket}/${s3Key}`
					window.open(link, '_blank')
				} else {
					throw new Error('S3 Upload error.')
				}
			}
		},
		onError: (e) => {
			setIsSubmitting(false)
			throw new Error(e)
		}
	})

	const handleDSFinError = () => {
		setIsDSFSubmitting(false)
		setIsDSFError(true)
		saveTabRef.current.close()
	}
	const [getDSFinExportData, DSFinExportRes] = useLazyQuery(getDSFinExport, {
		pollInterval: 2000,
		fetchPolicy: 'network-only',
		onError: e => {
			handleDSFinError()
			throw new Error(e)
		}
	})
	useEffect(() => {
		const queryName = getNameOfGqlQuery(getDSFinExport)
		const { status, error, file } = DSFinExportRes?.data?.[queryName] || {}
		if(status === 'READY' || status === 'ERROR') {
			DSFinExportRes.stopPolling()
			setIsDSFSubmitting(false)
		}
		if(file?.url) {
			// New tab opens when mutation starts to avoid browser popup blocks. And user can close this tab before request finished.
			// In this case we show modal with button to open another tab to download.
			if(!saveTabRef.current.closed) {
				saveTabRef.current.location = file?.url
			} else {
				setSaveTabUrl(file?.url)
			}
		} else if(error === 'No data') {
			saveTabRef.current.close()
			setShowNoDataModal(true)
		} else if(error) {
			handleDSFinError()
		}
	}, [DSFinExportRes.data])
	const [exportDSFin] = useMutation(exportDSFinMutation, {
		onCompleted: data => {
			const mutationName = getNameOfGqlQuery(exportDSFinMutation)
			getDSFinExportData({ variables: { requestId: data?.[mutationName] } })
		},
		onError: e => {
			handleDSFinError()
			throw new Error(e)
		}
	})

	const [exportDatev] = useLazyQuery(exportDatevQuery, {
		onCompleted: data => {
			const { success, messages, error } = data.cashAssist.sendDatev
			if (error) {
				setHasError(true)
			}
			if (!success && messages?.length) {
				setWarningMessages(messages)
			}
			setIsDatevLoading(false)
		},
		onError: e => {
			setIsDatevLoading(false)
			throw new Error(e)
		}
	})

	const [downloadDatev] = useLazyQuery(downloadDatevQuery, {
		onCompleted: data => {
        	const { success, messages, error } = data.cashAssist.downloadDatev
			if (error) {
				setHasError(true)
			}
			if (!success && messages?.length) {
				setWarningMessages(messages)
			}
			if (success) {
				downloadFromUrl(`${data?.cashAssist?.downloadDatev?.link}`, `${exportData.shop.name}.csv`)
			}
			setIsDatevDownloadLoading(false)
		},
		onError: e => {
			setIsDatevDownloadLoading(false)
			throw new Error(e)
		}
	})

	const [exportAbacus] = useLazyQuery(exportAbacusQuery, {
		onCompleted: data => {
			const { success, messages, error } = data.cashAssist.sendAbacus
			if (error) {
				setHasError(true)
			}
			if (!success && messages?.length) {
				setWarningMessages(messages)
			}
			setIsAbacusLoading(false)
		},
		onError: e => {
			setIsAbacusLoading(false)
			throw new Error(e)
		}
	})

	const [downloadAbacus] = useLazyQuery(downloadAbacusQuery, {
		onCompleted: data => {
			const { link: linkMain, linkCreditor, messages } = data.cashAssist.downloadAbacus
			downloadTwoExports(
				linkMain,
				linkCreditor,
				messages
			)
		},
		onError: e => {
			throw new Error(e)
		}
	})

	const [abacusErrors, setAbacusErrors] = useState(null)
	const downloadTwoExports = async (mainLink, secondaryLink, messages) => {
		if (mainLink) {
			await downloadFromUrlPromise(mainLink, 'abacusExport.xml')
		}
		if (secondaryLink) {
			await downloadFromUrlPromise(secondaryLink, 'abacusExport2nd.xml')
		}
		if (!mainLink || !secondaryLink) {
			setAbacusErrors(messages || null)
		}
		setIsAbacusDownloadLoading(false)
	}

	const handleSubmitDSF = vals => {
		setIsDSFSubmitting(true)
		saveTabRef.current = window.open('about:blank')
		exportDSFin({
			variables: {
				params: vals
			}
		})
	}

	const handleLoadDatev = () => {
		const { shop, email, datePeriod } = exportData

		const parsedShopId = JSON.parse(shop.id)
		const preparedShopId = Array.isArray(parsedShopId) ? parsedShopId : [shop.id]

		setIsDatevLoading(true)
		exportDatev({
			variables: {
				shopId: preparedShopId,
				email,
				datePeriod
			}
		})
	}

	const handleDownLoadDatev = () => {
		const { shop, datePeriod } = exportData

		const parsedShopId = JSON.parse(shop.id)
		const preparedShopId = Array.isArray(parsedShopId) ? parsedShopId : [shop.id]

		setIsDatevDownloadLoading(true)
		downloadDatev({
			variables: {
				shopId: preparedShopId,
				datePeriod
			}
		})
	}

	const handleLoadAbacus = () => {
		const { shop, email, datePeriod } = exportData

		const parsedShopId = JSON.parse(shop.id)
		const preparedShopId = Array.isArray(parsedShopId) ? parsedShopId : [shop.id]

		setIsAbacusLoading(true)
		exportAbacus({
			variables: {
				shopId: preparedShopId,
				email,
				datePeriod
			}
		})
	}

	const handleDownLoadAbacus = () => {
		const { shop, datePeriod } = exportData

		const parsedShopId = JSON.parse(shop.id)
		const preparedShopId = Array.isArray(parsedShopId) ? parsedShopId : [shop.id]

		setIsAbacusDownloadLoading(true)
		downloadAbacus({
			variables: {
				shopId: preparedShopId,
				datePeriod
			}
		})
	}

	const handleSubmit = vals => {
		const mutateVals = { ...vals }
		if(vals.cashRegisterId === 'null') {
			mutateVals.cashRegisterId = null
		}
		setIsSubmitting(true)
		mutate({ variables: vals })
	}
	return(
		<ExpandableBlocks>
			{
				!!hasError && <WarningModal
					onClose={() => setHasError(false)}
					label={t('options_section.export.header')}
					text={`${t('common.strings.no_closings')}`}
				/>
			}
			{
				!!warningMessages?.length && <WarningModal
					onClose={() => setWarningMessages([])}
					label={t('options_section.export.header')}
					text={`${t('options_section.export.text', { items: warningMessages.join(', ') })}`}
				/>
			}
			{props.appData.countryId !== '1' && // not for Switzerland
				<ContentBlock label='Datev'>
					<Form>
						<Row>
							<Col xs>
								<Select
									noForm
									value={exportData.shop}
									simpleValue={false}
									label={t('common.strings.branch')}
									onChange={(shop) => {
										setExportData(prevData => ({ ...prevData, shop }))
									}}
									options={shopsData}
									valueField='id'
									labelField='name'
									isSearchable
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
								<Text
									noForm
									value={exportData.email}
									onChange={({ target }) => {
										setExportData(prevData => ({ ...prevData, email: target?.value }))
									}}
									label={t('common.fields.email')}
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
							</Col>
							<Col xs>
								<DateTimePicker
									noForm
									value={exportData.datePeriod}
									onChange={(datePeriod) => setExportData(prevData => ({ ...prevData, datePeriod }))}
									label={t('common.fields.date')}
									validate={val => val ? null : t('validators.required')}
									validateOnChange
									interval
								/>
							</Col>
						</Row>
						<Divider />
						<Button
							status={exportData.shop.id && exportData.email?.length ? 'primary' : 'inactive'}
							text={t('message_section.strings.send')}
							loading={isDatevLoading}
							onClick={handleLoadDatev}
						/>
						<Button
							status={exportData.shop.id ? 'primary' : 'inactive'}
							text={t('common.buttons.download')}
							loading={isDatevDownloadLoading}
							onClick={handleDownLoadDatev}
						/>
					</Form>
				</ContentBlock>
			}
			{props.appData.countryId !== '2' && // not for Germany
				<ContentBlock label='Abacus'>
					<Form>
						<Row>
							<Col xs>
								<Select
									noForm
									value={exportData.shop}
									simpleValue={false}
									label={t('common.strings.branch')}
									onChange={(shop) => {
										setExportData(prevData => ({ ...prevData, shop }))
									}}
									options={shopsData}
									valueField='id'
									labelField='name'
									isSearchable
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
								<Text
									noForm
									value={exportData.email}
									onChange={({ target }) => {
										setExportData(prevData => ({ ...prevData, email: target?.value }))
									}}
									label={t('common.fields.email')}
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
							</Col>
							<Col xs>
								<DateTimePicker
									noForm
									value={exportData.datePeriod}
									onChange={(datePeriod) => setExportData(prevData => ({ ...prevData, datePeriod }))}
									label={t('common.fields.date')}
									validate={val => val ? null : t('validators.required')}
									validateOnChange
									interval
								/>
							</Col>
						</Row>
						<Divider />
						<Button
							status={exportData.shop.id && exportData.email?.length ? 'primary' : 'inactive'}
							text={t('message_section.strings.send')}
							loading={isAbacusLoading}
							onClick={handleLoadAbacus}
						/>
						<Button
							status={exportData.shop.id ? 'primary' : 'inactive'}
							text={t('common.buttons.download')}
							loading={isAbacusDownloadLoading}
							onClick={handleDownLoadAbacus}
						/>
					</Form>
					{abacusErrors?.length &&
						<ServiceModal
							type='confirm'
							onOk={() => {
								setAbacusErrors(null)
							}}
							onClose={() => {
								setAbacusErrors(null)
							}}
							text={abacusErrors?.join('. ')}
							btnText={t('common.buttons.confirm')}
							btnCancelText={t('common.buttons.cancel')}
						/>
					}
				</ContentBlock>
			}
			{props.appData.countryId !== '1' && // not for Switzerland
				<ContentBlock label='CSB'>
					<Form initialValues={initialValues} onSubmit={handleSubmit} getApi={api => (formApi.current = api)}>
						<Row>
							<Col xs>
								<Select
									field='cashRegisterId'
									label={t('common.fields.cash_register')}
									remoteOptions={{
										query: queryCrs,
										additionalOptions: [{ id: 'null', name: t('statistic_section.strings.all_cash_registers') }]
									}}
									valueField='id'
									labelField='name'
									isSearchable
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
								<Select
									field='type'
									label={t('common.fields.type')}
									options={[
										{ value: 'closing', label: 'LABO_UM' },
										{ value: 'userTime', label: 'BED_ZEIT' }
									]}
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
								<Checkbox
									field='isTemporary'
									label={t('options_section.fields.download_file')}
									checked={saveFile}
									onChange={e => setSaveFile(e.target.checked)}
								/>
							</Col>
							<Col xs>
								<DateTimePicker
									field='date'
									label={t('common.fields.date')}
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
							</Col>
						</Row>
						<Divider />
						<Button
							type='submit'
							text={t('common.buttons.apply')}
							loading={isSubmitting}
						/>
					</Form>
				</ContentBlock>
			}
			{props.appData.countryId !== '1' && // not for Switzerland
				<ContentBlock label={t('options_section.strings.dsfin')}>
					<Form initialValues={{ period: { from: startDay, to: endDay } }} onSubmit={handleSubmitDSF}>
						<Row>
							<Col xs>
								<Select
									field='cashRegisterId'
									label={t('common.fields.cash_register')}
									remoteOptions={{
										query: queryCrs
									}}
									valueField='id'
									labelField='name'
									isSearchable
									validate={val => val ? null : t('validators.required')}
									validateOnChange
								/>
							</Col>
							<Col xs>
								<DateTimePicker
									field='period'
									label={t('common.fields.date')}
									validate={val => val ? null : t('validators.required')}
									validateOnChange
									interval
								/>
							</Col>
						</Row>
						<Divider />
						<Button
							type='submit'
							text={t('common.buttons.apply')}
							loading={isDSFSubmitting}
						/>
					</Form>
					{saveTabUrl &&
						<ServiceModal
							type='confirm'
							onOk={() => {
								window.open(saveTabUrl, '_blank')
								setSaveTabUrl(null)
							}}
							onClose={() => setSaveTabUrl(null)}
							text={t('common.strings.press_if_no_save')}
							btnText={t('options_section.fields.download_file')}
							btnCancelText={t('common.buttons.abort')}
						/>
					}
					{isDSFError &&
						<AlertModal
							onClose={() => setIsDSFError(false)}
							text={`${t('common.strings.table_no_data')} ${t('common.strings.or')} ${t('common.strings.network_error')}.`}
						/>
					}
					{showNoDataModal &&
						<WarningModal
							onClose={() => setShowNoDataModal(false)}
							text={`${t('common.strings.table_no_data')}.`}
						/>
					}
				</ContentBlock>
			}
			{props.appData.countryId !== '2' && // not for Germany
				<ContentBlock label='Bexio'>
					<Bexio
						formData={props.formData}
						onUpdate={props.onUpdate}
					/>
				</ContentBlock>
			}
			{props.appData.countryId !== '2' && // not for Germany
				<ContentBlock label='Apaleo'>
					<Apaleo/>
				</ContentBlock>
			}
			{props.appData.countryId !== '2' && // not for Germany
				<Frutico appData={props.appData}/>
			}
			<StatusIndicator status={isDSFSubmitting ? 'loading' : isDSFError ? 'error' : null} />
		</ExpandableBlocks>
	)
}

export default Export
